/**
 * Per-Meta-ad detail route (#5981) — the Meta sibling of
 * ``routes/.../google/$campaignslug/_slug/ad/$adId.tsx``.
 *
 * Same shell as the Meta campaign route (sidebar left, detail panel right); the panel scopes to
 * one ad. The ``$metaCampaignSlug_`` segment opts out of nesting under the campaign route file
 * (which renders no <Outlet>), while keeping the ``/campaigns/meta/{slug}/ad/{id}`` path.
 */

import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Megaphone } from 'lucide-react';

import { campaignDataAtom, metaCampaignDataAtom } from '@/store';
import { CampaignSidebar, type PlatformFilter } from '@/components/campaign/campaign-sidebar';
import { MetaAdDetailPanel } from '@/components/campaign/meta/meta-ad-detail-panel';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { FilterDropdown, type FilterDropdownState } from '@/components/glitch-ui/filter-dropdown';
import { PageEmptyState } from '@/components/glitch-ui/empty-state';
import { useTestMode } from '@/hooks/useTestMode';
import { useCampaignSidebarFilters } from '@/hooks/useCampaignSidebarFilters';
import googleAdsLogo from '/google-ads.svg';
import metaLogo from '/meta.png';

export const Route = createFileRoute('/_authenticated/_layout/campaigns/meta/$metaCampaignSlug_/ad/$adId')({
  component: MetaAdPage,
});

function MetaAdPage() {
  const { metaCampaignSlug, adId } = useParams({
    from: '/_authenticated/_layout/campaigns/meta/$metaCampaignSlug_/ad/$adId',
  });
  const isTestMode = useTestMode();
  const setMetaCampaignData = useSetAtom(metaCampaignDataAtom);
  const setCampaignData = useSetAtom(campaignDataAtom);

  // Keep the campaign selected in the sidebar (and expanded to its ads) while an ad is open —
  // mirrors the Google ad route keeping campaignDataAtom pointed at the parent campaign (#4660).
  useEffect(() => {
    setMetaCampaignData(metaCampaignSlug);
    setCampaignData(null);
  }, [metaCampaignSlug, setMetaCampaignData, setCampaignData]);

  // The persisted sidebar filters + FilterDropdown the Google ad route carries (#4446) — this
  // route previously hardcoded the filter sets and offered no filter control (#6163 P2, #6182).
  const { filterTypes, filterStatuses, filterPlatforms, updateFilters, HIDDEN_TYPES } = useCampaignSidebarFilters();
  const platformFilter: PlatformFilter =
    filterPlatforms.size === 1 && filterPlatforms.has('google') ? 'google' :
    filterPlatforms.size === 1 && filterPlatforms.has('meta') ? 'meta' :
    'all';

  // #6257: Meta UI is gated behind test mode until Meta App Review clears (the pre-GA guard from
  // #5976, revived). Deep links land here safely; the backend keeps its own flag regardless.
  if (!isTestMode) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3" />
        </div>
        <div className="flex-1">
          <PageEmptyState
            icon={<Megaphone className="h-12 w-12 text-gray-400" />}
            title="Meta Campaigns"
            description="The Meta Ads campaign workspace is not available for this organization yet."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>
      <div className="flex-1">
        <div className="grid grid-cols-12 gap-1">
          <div className="col-span-3 sticky top-0 self-start h-[calc(100vh-120px)]">
            <TabCardPanel
              tabs={[{ id: 'all', label: 'Campaigns', isActive: true }]}
              actions={
                <FilterDropdown
                  sections={[
                    {
                      id: 'platforms',
                      title: 'Platform',
                      options: [
                        { value: 'google', label: 'Google', icon: <img src={googleAdsLogo} alt="" className="h-4 w-4" /> },
                        { value: 'meta', label: 'Meta', icon: <img src={metaLogo} alt="" className="w-5 h-auto" /> },
                      ],
                    },
                    { id: 'statuses', title: 'Status', options: [
                      { value: 'live', label: 'Live' },
                      { value: 'draft', label: 'Draft' },
                      { value: 'paused', label: 'Paused' },
                      { value: 'archived', label: 'Archived' },
                    ]},
                    { id: 'types', title: 'Type', options: [
                      { value: 'brand', label: 'Brand' },
                      { value: 'prospecting', label: 'Prospecting' },
                      { value: 'competitor', label: 'Competitor' },
                      { value: 'hipo', label: 'High Performance' },
                      { value: 'icp', label: 'ICP' },
                      { value: 'location', label: 'Location' },
                      { value: 'external', label: 'External' },
                      { value: 'retargeting', label: 'Retargeting' },
                    ]},
                  ]}
                  selectedValues={{
                    platforms: [...filterPlatforms],
                    statuses: [...filterStatuses],
                    types: [...filterTypes].filter((t) => !HIDDEN_TYPES.includes(t)),
                  }}
                  hiddenValues={{ types: HIDDEN_TYPES }}
                  showDateRange={false}
                  popoverWidth="w-[220px]"
                  onApply={(filters: FilterDropdownState) => {
                    updateFilters(
                      new Set(filters.selections.types || []),
                      new Set(filters.selections.statuses || []),
                      new Set(filters.selections.platforms || []),
                    );
                  }}
                />
              }
              cardClassName="overflow-hidden"
              noPadding
            >
              <CampaignSidebar
                filterTypes={filterTypes}
                filterStatuses={filterStatuses}
                platformFilter={platformFilter}
              />
            </TabCardPanel>
          </div>
          <div className="col-span-9">
            <MetaAdDetailPanel slug={metaCampaignSlug} adId={Number(adId)} />
          </div>
        </div>
      </div>
    </div>
  );
}
